import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star, ChevronRight, Copy, Check, ExternalLink, Loader2,
  MapPin, Wrench, ThumbsUp, MessageSquare, Sparkles, CheckCircle2,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewCoaching() {
  const { requestId } = useParams();
  const [step, setStep] = useState('loading'); // loading, service, details, generating, draft, error
  const [config, setConfig] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [bestPart, setBestPart] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [draft, setDraft] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/reviews/coaching/${requestId}`);
        setConfig(res.data);
        setStep('service');
      } catch (e) {
        setError('This review link is invalid or has expired.');
        setStep('error');
      }
    };
    fetchConfig();
  }, [requestId]);

  const handleGenerateDraft = async () => {
    setStep('generating');
    try {
      const res = await axios.post(`${API}/reviews/generate-draft`, {
        request_id: requestId,
        service_type: serviceType,
        neighborhood,
        best_part: bestPart,
        additional_comments: additionalComments,
      });
      setDraft(res.data);
      setStep('draft');
    } catch (e) {
      setError('Failed to generate review draft. Please try again.');
      setStep('error');
    }
  };

  const handleCopy = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.review_draft);
      setCopied(true);
      // Track that draft was copied
      axios.post(`${API}/reviews/coaching/${requestId}/copied`).catch(() => {});
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = draft.review_draft;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const openGoogleReviews = () => {
    const bizName = encodeURIComponent(draft?.business_name || config?.business_name || '');
    const city = encodeURIComponent(config?.city || '');
    window.open(`https://www.google.com/search?q=${bizName}+${city}+reviews`, '_blank');
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1D6B4F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <div className="w-14 h-14 rounded-full bg-[#E24B4A]/10 flex items-center justify-center mb-4">
          <Star size={24} className="text-[#E24B4A]" />
        </div>
        <p className="text-sm text-[#E24B4A] font-medium text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EF]" data-testid="review-coaching-page">
      {/* Header */}
      <div className="px-6 pt-8 pb-4 text-center">
        <div className="w-12 h-12 rounded-none bg-[#C67A5C]/10 flex items-center justify-center mx-auto mb-3">
          <Star size={22} strokeWidth={1.5} className="text-[#C67A5C]" />
        </div>
        <h1 className="font-display text-xl font-medium text-[#3C3226]" data-testid="coaching-heading">
          Share your experience with {config?.business_name}
        </h1>
        <p className="text-sm text-[#5C5347] mt-1">
          Takes about 30 seconds — we'll help you write a great review
        </p>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {['service', 'details', 'draft'].map((s, i) => {
          const steps = ['service', 'details', 'draft'];
          const currentIdx = steps.indexOf(step === 'generating' ? 'draft' : step);
          return (
            <div
              key={s}
              className={`h-1.5 rounded-none transition-all duration-300 ${
                i <= currentIdx ? 'w-8 bg-[#C67A5C]' : 'w-4 bg-[#D4C9B8]'
              }`}
              data-testid={`progress-step-${s}`}
            />
          );
        })}
      </div>

      <div className="px-6 max-w-lg mx-auto pb-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Service Type */}
          {step === 'service' && (
            <motion.div
              key="service"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              data-testid="step-service"
            >
              <div className="flex items-center gap-2 mb-4">
                <Wrench size={16} strokeWidth={1.5} className="text-[#1D6B4F]" />
                <h2 className="text-sm font-medium text-[#3C3226]">What service did you receive?</h2>
              </div>
              <div className="space-y-2">
                {(config?.service_options || []).map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setServiceType(opt.id)}
                    className={`w-full text-left p-4 rounded-none border transition-all ${
                      serviceType === opt.id
                        ? 'bg-[#1D6B4F]/5 border-[#1D6B4F]/30 ring-1 ring-[#1D6B4F]/20'
                        : 'bg-[#F7F4EF] border-[#D4C9B8] hover:border-[#1D6B4F]/20'
                    }`}
                    data-testid={`service-option-${opt.id}`}
                  >
                    <span className={`text-sm ${serviceType === opt.id ? 'text-[#1D6B4F] font-medium' : 'text-[#3C3226]'}`}>
                      {opt.name}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep('details')}
                disabled={!serviceType}
                className="w-full mt-6 rounded-none bg-[#3C3226] hover:opacity-85 disabled:opacity-40 text-[#F0EBE3] py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                data-testid="next-to-details-btn"
              >
                Continue <ChevronRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 2: Details */}
          {step === 'details' && (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              data-testid="step-details"
            >
              {/* Neighborhood */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={16} strokeWidth={1.5} className="text-[#1D6B4F]" />
                  <label className="text-sm font-medium text-[#3C3226]">Your neighborhood or area</label>
                </div>
                <input
                  type="text"
                  value={neighborhood}
                  onChange={e => setNeighborhood(e.target.value)}
                  placeholder={config?.city ? `e.g., ${config.city}` : 'e.g., Capitol Hill'}
                  className="w-full rounded-none border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                  data-testid="neighborhood-input"
                />
              </div>

              {/* Best part */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsUp size={16} strokeWidth={1.5} className="text-[#1D6B4F]" />
                  <label className="text-sm font-medium text-[#3C3226]">What was the best part?</label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(config?.best_part_options || []).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setBestPart(opt.id)}
                      className={`text-left p-3 rounded-sm border text-xs transition-all ${
                        bestPart === opt.id
                          ? 'bg-[#1D6B4F]/5 border-[#1D6B4F]/30 text-[#1D6B4F] font-medium'
                          : 'bg-[#F7F4EF] border-[#D4C9B8] text-[#3C3226] hover:border-[#1D6B4F]/20'
                      }`}
                      data-testid={`best-part-${opt.id}`}
                    >
                      {opt.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional comments */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={16} strokeWidth={1.5} className="text-[#5C5347]" />
                  <label className="text-sm text-[#5C5347]">Anything else? (optional)</label>
                </div>
                <textarea
                  value={additionalComments}
                  onChange={e => setAdditionalComments(e.target.value)}
                  placeholder="Any other details you'd like to mention..."
                  rows={3}
                  className="w-full rounded-none border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none resize-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                  data-testid="additional-comments"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('service')}
                  className="flex-1 rounded-full border border-[#D4C9B8] text-[#5C5347] py-3.5 text-sm font-medium hover:bg-[#EDE8DF] transition-colors"
                  data-testid="back-to-service-btn"
                >
                  Back
                </button>
                <button
                  onClick={handleGenerateDraft}
                  disabled={!bestPart}
                  className="flex-[2] rounded-none bg-[#C67A5C] hover:bg-[#B3694C] disabled:opacity-40 text-[#F0EBE3] py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="generate-draft-btn"
                >
                  <Sparkles size={15} /> Write my review
                </button>
              </div>
            </motion.div>
          )}

          {/* Generating */}
          {step === 'generating' && (
            <motion.div
              key="generating"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
              data-testid="step-generating"
            >
              <div className="w-16 h-16 rounded-none bg-[#C67A5C]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={28} className="text-[#C67A5C] animate-spin" />
              </div>
              <p className="text-sm font-medium text-[#3C3226] mb-1">Crafting your review...</p>
              <p className="text-xs text-[#5C5347]">Making sure it includes all the right details</p>
            </motion.div>
          )}

          {/* Step 3: Draft */}
          {step === 'draft' && draft && (
            <motion.div
              key="draft"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-draft"
            >
              <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8] mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={16} className="text-[#1D6B4F]" />
                  <p className="text-xs font-medium text-[#1D6B4F]">Your review is ready!</p>
                </div>
                <p
                  className="text-sm text-[#3C3226] leading-relaxed whitespace-pre-wrap"
                  data-testid="review-draft-text"
                >
                  {draft.review_draft}
                </p>
              </div>

              {/* What this review mentions */}
              <div className="p-4 rounded-none bg-[#1D6B4F]/5 border border-[#1D6B4F]/10 mb-6">
                <p className="text-xs font-medium text-[#1D6B4F] mb-2">This review mentions:</p>
                <div className="flex flex-wrap gap-2">
                  {draft.service_name && (
                    <span className="inline-flex items-center gap-1 text-xs bg-[#F7F4EF] rounded-full px-3 py-1 text-[#3C3226] border border-[#D4C9B8]">
                      <Check size={11} className="text-[#1D6B4F]" /> {draft.service_name}
                    </span>
                  )}
                  {draft.neighborhood && (
                    <span className="inline-flex items-center gap-1 text-xs bg-[#F7F4EF] rounded-full px-3 py-1 text-[#3C3226] border border-[#D4C9B8]">
                      <Check size={11} className="text-[#1D6B4F]" /> {draft.neighborhood}
                    </span>
                  )}
                  {draft.business_name && (
                    <span className="inline-flex items-center gap-1 text-xs bg-[#F7F4EF] rounded-full px-3 py-1 text-[#3C3226] border border-[#D4C9B8]">
                      <Check size={11} className="text-[#1D6B4F]" /> {draft.business_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={handleCopy}
                  className={`w-full rounded-full py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    copied
                      ? 'bg-[#1D6B4F] text-[#F0EBE3]'
                      : 'bg-[#3C3226] hover:opacity-85 text-[#F0EBE3]'
                  }`}
                  data-testid="copy-review-btn"
                >
                  {copied ? <><Check size={15} /> Copied to clipboard!</> : <><Copy size={15} /> Copy review</>}
                </button>
                <button
                  onClick={openGoogleReviews}
                  className="w-full rounded-none bg-[#C67A5C] hover:bg-[#B3694C] text-[#F0EBE3] py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="post-to-google-btn"
                >
                  <ExternalLink size={15} /> Post to Google Reviews
                </button>
                <button
                  onClick={() => setStep('details')}
                  className="w-full rounded-full border border-[#D4C9B8] text-[#5C5347] py-3 text-xs hover:bg-[#EDE8DF] transition-colors"
                  data-testid="edit-review-btn"
                >
                  Edit details and regenerate
                </button>
              </div>

              <p className="text-center text-xs text-[#5C5347] mt-6">
                Thank you for supporting {config?.business_name}!
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
